import { useState } from 'react';
import EcoLink from 'eco-design/base/eco-link';
import { useRouter } from 'next/router';
import { subscribeCookie } from 'eco-design-reliance/cookies/subscribeCookie';
import gtm from 'eco-design-reliance/gtm';
import EcoIconCheckboxN from 'eco-design-micro/icons/eco-icon-checkbox-n';
import EcoIconCheckboxY from 'eco-design-micro/icons/eco-icon-checkbox-y';
import { sendMultiEmarsysEmail } from './api';
import { getTermsLink, getSubscribe, INIT_TIP, TIP_EMAIL_EMPTY, TIP_EMAIL_ILLEGAL, TIP_CHECKBOX, TIP_SUCCESS } from './config';
import css from './index.module.scss';
import { ItfCompProps, ItfSubscribeTip } from './type';
import { useLang } from '@/i18n';

export const SceneEmail: React.FC<ItfCompProps> = ({ theme = 'white', type, title, subscribeData }) => {
    const { locale = '' } = useRouter();
    const { t } = useLang('powerbackup');
    const [tip, setTip] = useState<ItfSubscribeTip>(INIT_TIP);
    const [input, setInput] = useState<string>('');
    const [checked, setChecked] = useState<boolean>(false);
    const links: string[] = getTermsLink(locale);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const handleFocus = () => {
        if (tip?.text !== TIP_CHECKBOX) {
            setTip(INIT_TIP);
        }
    };

    const handleCheck = () => {
        if (!checked && tip?.text === TIP_CHECKBOX) {
            setTip(INIT_TIP);
        }
        setChecked(!checked);
    };

    const doValidate = () => {
        const email = input?.trim();
        if (!email) {
            setTip({
                type: 'error',
                text: TIP_EMAIL_EMPTY,
            });
            return false;
        }
        const regEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!regEmail.test(email)) {
            setTip({
                type: 'error',
                text: TIP_EMAIL_ILLEGAL,
            });
            return false;
        }
        if (!checked) {
            setTip({
                type: 'error',
                text: TIP_CHECKBOX,
            });
            return false;
        }
        return true;
    };

    const doGtm = (status: 'success' | 'fail') => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_submit',
            button_name: 'Subscribe',
            status,
            tag: 'Agree',
        });
        // 订阅结果埋点：成功 subscribe_success / 失败 subscribe_fail
        console.log(`subscribe_${status}`); // TODO: 自测用，联调后删除
        gtm.push({
            event: 'uaEvent',
            event_name: `subscribe_${status}`,
            button_name: 'Subscribe',
            status,
            tag: 'Agree',
            product_name: 'none',
            __btn_id1: 'scene-email',
            __btn_id2: 'Subscribe',
            __btn_type: 'text',
            module_name: 'scene email',
            entry_type: 'fixed entry',
            entry_position: 'none',
            entry_name: window.location.href,
        });
    };

    const handleSubmit = async () => {
        // 点击订阅按钮埋点
        console.log('click_subscribe'); // TODO: 自测用，联调后删除
        gtm.push({
            event: 'uaEvent',
            event_name: 'click_subscribe',
            button_name: 'Subscribe',
            tag: checked ? 'Agree' : 'Disagree',
            __btn_id1: 'scene-email',
            __btn_id2: 'Subscribe',
            __btn_type: 'text',
            module_name: 'scene email',
            entry_type: 'section_entry',
            entry_position: '1',
            entry_name: window.location.href,
        });

        if (!doValidate()) return;
        const email = input?.trim();
        const res = await sendMultiEmarsysEmail(
            locale,
            {
                ...(subscribeData ? { ...subscribeData, email } : getSubscribe(type, email, locale)),
                otherContactListIds: [],
            },
            subscribeCookie({
                sector_name: title,
                entry_position: 1,
            }),
        );
        if (res?.code === '0') {
            setTip({ type: 'success', text: TIP_SUCCESS });
            const timer = setTimeout(() => {
                setTip(INIT_TIP);
                setInput('');
                setChecked(false);
                clearTimeout(timer);
            }, 3000);
            doGtm('success');
        } else {
            setTip({
                type: 'error',
                text: res?.message as string | 'Something went wrong in the backend!',
            });
            doGtm('fail');
        }
    };

    return (
        <div className={`scene-email ${css['scene-email']} theme-${theme}`}>
            <p className="scene-text">{t('scene_email_text')}</p>
            <div className="scene-control">
                <div className="scene-inner">
                    <div className="scene-input-wrapper">
                        <input
                            id="scene-email-input"
                            name="email"
                            type="text"
                            className="scene-input"
                            placeholder="Enter your email"
                            value={input}
                            onChange={handleInput}
                            onFocus={handleFocus}
                        />
                        <button className="scene-btn" onClick={handleSubmit}>
                            Subscribe
                        </button>
                    </div>
                    {tip?.text && <p className={`scene-tip tip-${tip?.type}`}>{tip?.text}</p>}
                </div>
                <div className="scene-terms-wrapper" onClick={handleCheck}>
                    {/* <span className={`scene-checkbox${checked ? ' checked' : ''}`}></span> */}
                    {checked && <EcoIconCheckboxY />}
                    {!checked && <EcoIconCheckboxN />}
                    <div className="scene-terms-text">
                        * I agree to EcoFlow&apos;s{' '}
                        <EcoLink
                            stopPropagation
                            className="GtmModuleData-btn-href"
                            dataLayer={{
                                __btn_id1: 'scene-email',
                                __btn_id2: links[0],
                                __btn_type: 'href',
                            }}
                            href={links[0]}
                        >
                            Privacy Policy
                        </EcoLink>{' '}
                        and{' '}
                        <EcoLink
                            stopPropagation
                            className="GtmModuleData-btn-href"
                            dataLayer={{
                                __btn_id1: 'scene-email',
                                __btn_id2: links[1],
                                __btn_type: 'href',
                            }}
                            href={links[1]}
                        >
                            Terms of Service
                        </EcoLink>
                        .
                    </div>
                </div>
            </div>
        </div>
    );
};
